import { useTranslation } from 'react-i18next';
import styles from './SettingsPage.module.css';
import Text from '../../componentsNew/atoms/typography/Text';
import { ColorEnum, FontSizeEnum, FontWeightEnum } from '../../componentsNew/enums';
import { Data, FormErrors } from '../../componentsNew/molecules/form/Form';
import FormError from '../../componentsNew/molecules/form/FormError';
import { RadioInput } from '../../componentsNew/atoms/radio-input';
import SkeletonComponent from '../../components/SkeletonComponent';
import {CheckboxInput} from '../../componentsNew/molecules/checkbox-input';
import stringToBoolean from '../../componentsNew/utils/stringToBoolean';

interface EmailNotificationsContentProps {
    formData: Data;
    formErrors: FormErrors[];
    isLoading?: boolean;
    agencyEmail: string;
}

const EmailNotificationsContent = ({
    formData,
    formErrors,
    isLoading = false,
    agencyEmail
}: EmailNotificationsContentProps) => {
    const { t } = useTranslation();

    const renderRecipientsNotificationsSection = () => {
        const options = [
            { label: t('settingsPage.notifications.recipients.agentAndAgency', { email: agencyEmail }), value: 'agency_and_agent' },
            { label: t('settingsPage.notifications.recipients.agent'), value: 'agent' },
            { label: t('settingsPage.notifications.recipients.agency', { email: agencyEmail }), value: 'agency' },
            { label: t('settingsPage.notifications.recipients.none'), value: 'none' },
        ];

        return (
            <div className={styles.contentSection}>
                <div className={styles.descriptionPanel}>
                    <Text
                        color={ColorEnum.Gray800}
                        weight={FontWeightEnum.Bold}
                        size={FontSizeEnum.Lg}
                        tag='h6'
                    >
                        {t('setttingsPage.notifications.recipients')}
                    </Text>
                    <Text
                        size={FontSizeEnum.Md}
                        color={ColorEnum.Gray800}
                        tag='p'
                    >
                        {t('settingsPage.notifications.recipients.description')}
                    </Text>
                </div>
                <div className={styles.settingsPanel}>
                    <div className={styles.inputSectionLabel}>
                        <Text
                            size={FontSizeEnum.Md}
                            color={ColorEnum.Gray800}
                            weight={FontWeightEnum.Semibold}
                            tag='p'
                        >
                            {t('settingsPage.notifications.recipients.label')}
                        </Text>
                    </div>
                    {isLoading
                        ?
                        <div className={styles.radioButtonSkeletonContainer}>
                            <SkeletonComponent height={24} width={70}/>
                            <SkeletonComponent height={24} width={70}/>
                        </div>
                        :
                        <RadioInput
                            required
                            options={options}
                            name={'viewingNotificationRecipientGroup'}
                            value={formData['viewingNotificationRecipientGroup']?.value?.toString()  ?? 'agency_and_agent'}
                        />
                    }
                    <FormError errors={formErrors['viewingNotificationRecipientGroup'] || []} />
                </div>
            </div>
        );
    }

    const renderTypeNotificationsSection = () => {
        return <div className={styles.contentSection}>
            <div className={styles.descriptionPanel}>
                <Text
                    size={FontSizeEnum.Lg}
                    color={ColorEnum.Gray800}
                    weight={FontWeightEnum.Bold}
                    tag='h6'
                >
                    {t('settingsPage.notifications.type')}
                </Text>
                <Text
                    size={FontSizeEnum.Md}
                    color={ColorEnum.Gray800}
                    tag='p'
                >
                    {t('settingsPage.notifications.type.description')}
                </Text>
            </div>
            <div className={styles.settingsPanel}>
                <div className={styles.inputSectionLabel}>
                    <Text
                        size={FontSizeEnum.Md}
                        color={ColorEnum.Gray800}
                        weight={FontWeightEnum.Semibold}
                        tag='p'
                    >
                        {t('settingsPage.notifications.type.label')}
                    </Text>
                </div>
                {isLoading
                    ?
                    <SkeletonComponent height={40}/>
                    :
                    <div>
                        <div className={styles.checkboxInputSectionWrapper}>
                            <CheckboxInput
                                name={'viewingRejectedNotificationEnabled'}
                                label={t('settingsPage.notifications.type.rejected')}
                                checked={stringToBoolean(formData['viewingRejectedNotificationEnabled']?.value as string)}
                            />
                            <CheckboxInput
                                name={'viewingPlannedNotificationEnabled'}
                                label={t('settingsPage.notifications.type.planned')}
                                checked={stringToBoolean(formData['viewingPlannedNotificationEnabled']?.value as string)}
                            />
                            <CheckboxInput
                                name={'viewingRescheduledNotificationEnabled'}
                                label={t('settingsPage.notifications.type.rescheduled')}
                                checked={stringToBoolean(formData['viewingRescheduledNotificationEnabled']?.value as string)}
                            />
                        </div>
                        <div>
                            <div className={styles.inputSectionLabel}>
                                <Text
                                    size={FontSizeEnum.Md}
                                    color={ColorEnum.Gray800}
                                    weight={FontWeightEnum.Semibold}
                                    tag='p'
                                >
                                    {t('settingsPage.notifications.type.attendanceList.label')}
                                </Text>
                            </div>
                            <CheckboxInput
                                name={'viewingAttendanceListEnabled'}
                                label={t('settingsPage.notifications.type.attendanceList')}
                                checked={stringToBoolean(formData['viewingAttendanceListEnabled']?.value as string)}
                            />
                        </div>
                    </div>
                }
                <FormError errors={formErrors['viewingRejectedNotificationEnabled'] || []} />
                <FormError errors={formErrors['viewingPlannedNotificationEnabled'] || []} />
                <FormError errors={formErrors['viewingRescheduledNotificationEnabled'] || []} />
                <FormError errors={formErrors['ViewingAttendanceListEnabled'] || []} />
            </div>
        </div>;
    };

    return (
        <>
            {renderRecipientsNotificationsSection()}
            <hr/>
            {renderTypeNotificationsSection()}
        </>
    );
};

export default EmailNotificationsContent;
